import React, { ReactElement, ReactNode } from 'react';
import i18next from 'i18next';
import { clamp, formatMinutes, formatPercentage } from '../../util/number-utils';

export type TrainingProgressBarProps = {
  totalMinutes: number;
  totalTargetMinutes: number;
  totalPercentage: number;
  normalMinutes: number;
  enduranceMinutes: number;
  countedEnduranceMinutes: number;
  enduranceLimitMinutes: number;
  maximumEnduranceTrainingPercentage: number;
  endurancePercentage: number;
  className?: string;
};

export type TrainingProgressBadgeProps = {
  totalPercentage: number;
};

type LegendItemProps = {
  color: string;
  label: string;
  value: string;
};

const BAR_TRANSITION: string = 'width 700ms ease-out';

export function TrainingProgressBadge({ totalPercentage }: TrainingProgressBadgeProps): ReactElement {
  return (
    <span className="badge rounded-pill bg-primary-subtle text-primary fw-semibold">
      {i18next.t('patientsTable.trainingProgressPercentage', { value: formatPercentage(totalPercentage) })}
    </span>
  );
}

function TrainingProgressBar({
  totalMinutes,
  totalTargetMinutes,
  totalPercentage,
  normalMinutes,
  enduranceMinutes,
  countedEnduranceMinutes,
  enduranceLimitMinutes,
  maximumEnduranceTrainingPercentage,
  endurancePercentage,
  className = '',
}: TrainingProgressBarProps): ReactElement {
  const strengthLabel: string = i18next.t('patientsTable.normalTraining');
  const enduranceShareLabel: string = i18next.t('patientsTable.trainingProgressEnduranceShare');

  const share = (minutes: number): number =>
    totalTargetMinutes > 0 ? clamp((minutes / totalTargetMinutes) * 100, 0, 100) : 0;

  const strengthWidth: number = share(normalMinutes);
  const enduranceWidth: number = clamp(share(countedEnduranceMinutes), 0, 100 - strengthWidth);

  const totalLabel: string = i18next.t('patientsTable.trainingProgressTotal', {
    minutes: formatMinutes(totalMinutes),
    target: formatMinutes(totalTargetMinutes),
  });

  return (
    <div className={className}>
      <div
        role="progressbar"
        aria-valuenow={clamp(totalPercentage, 0, 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={totalLabel}
        className="d-flex rounded-pill overflow-hidden"
        style={{ height: '0.75rem', backgroundColor: 'var(--bs-secondary-bg)' }}
      >
        <div style={{ width: `${strengthWidth}%`, backgroundColor: 'var(--bs-info)', transition: BAR_TRANSITION }} />
        <div style={{ width: `${enduranceWidth}%`, backgroundColor: 'var(--bs-primary)', transition: BAR_TRANSITION }} />
      </div>

      <div className="d-flex flex-wrap align-items-center gap-3 mt-2 small">
        <span className="fw-semibold text-nowrap">{totalLabel}</span>
        <LegendItem
          color="var(--bs-info)"
          label={strengthLabel}
          value={i18next.t('patientsTable.trainingProgressMinutes', { value: formatMinutes(normalMinutes) })}
        />
        <LegendItem
          color="var(--bs-primary)"
          label={enduranceShareLabel}
          value={i18next.t('patientsTable.trainingProgressMinutes', { value: formatMinutes(countedEnduranceMinutes) })}
        />
      </div>

      {enduranceLimitMinutes > 0 && (
        <>
          <hr className="my-2" />
          <div className="d-flex flex-wrap align-items-center gap-2 small text-body-secondary">
            <span className="text-nowrap">
              {i18next.t('patientsTable.enduranceQuota', { percentage: formatPercentage(maximumEnduranceTrainingPercentage) })}
            </span>
            <div
              role="progressbar"
              aria-valuenow={clamp(endurancePercentage, 0, 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              className="rounded-pill overflow-hidden flex-grow-1"
              style={{ height: '0.375rem', minWidth: '3rem', backgroundColor: 'var(--bs-secondary-bg)' }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${clamp(endurancePercentage, 0, 100)}%`,
                  backgroundColor: 'var(--bs-primary)',
                  opacity: 0.55,
                  transition: BAR_TRANSITION,
                }}
              />
            </div>
            <span className="text-nowrap text-body">
              {i18next.t('patientsTable.enduranceQuotaUsed', {
                minutes: formatMinutes(enduranceMinutes),
                limit: formatMinutes(enduranceLimitMinutes),
              })}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function LegendItem({ color, label, value }: LegendItemProps): ReactElement {
  const swatch: ReactNode = (
    <span
      style={{ display: 'inline-block', width: '0.625rem', height: '0.625rem', borderRadius: '3px', backgroundColor: color }}
    />
  );

  return (
    <span className="d-inline-flex align-items-center gap-2 text-nowrap">
      {swatch}
      <span className="fw-semibold">{label}</span>
      <span className="text-body-secondary">{value}</span>
    </span>
  );
}

export default TrainingProgressBar;
